import { create } from 'zustand';
import { getLocalTimeZone, today, CalendarDate } from '@internationalized/date';
import { Dependent } from '../data/dependent';
import { LoginUser } from '../data/loginUser';
import { Subscriber } from '../data/subscriber';
import { FamilyInfo, TobaccoInfo } from '../types/tobacco-use.types';
import {
  isEmpty,
  isOutsideOfDateRange,
  // offExchange,
  // onExchange,
} from '../helper/helpers';

interface TobaccoUseStore {
  tobaccoApiResponse: FamilyInfo;
  setTobaccoApiResponse: (state: any) => void;
  loginUser: LoginUser;
  setLoginUserTobaccoIndicator: (state: any) => void;
  setLoginUserLastUseDate: (date: any) => void;
  dependents: Array<Dependent>;
  setDependentTobaccoIndicator: (tobaccoInd: any, dependentId: any) => void;
  setDependentLastUseDate: (date: any, dependentId: any) => void;
  initData: () => void;
  isBelowMinAgeLimit: boolean;
  setIsBelowMinAgeLimit: (state: boolean) => void;
  isDisabled: boolean;
  setIsDisabled: (state: boolean) => void;
  toggleIsDisabled: () => void;
  offExchangeVal: boolean;
  setOffExchangeVal: (state: boolean) => void;
  onExchangeVal: boolean;
  setOnExchangeVal: (state: boolean) => void;
}

const tobaccoStore = create<TobaccoUseStore>((set: any) => ({
  tobaccoApiResponse: {
    clientNumber: '',
    clientId: '',
    groupNumber: '',
    groupId: '',
    internalGroupId: '',
    tobaccoList: [],
  },
  setTobaccoApiResponse: (state: any) => {
    set({
      tobaccoApiResponse: state,
    });
  },
  loginUser: {
    isOverTobaccoAgeLimit: false,
    tobaccoIndicator: '',
    tobaccoUseEffectiveBeginDate: '',
    tobaccoUseEffectiveEndDate: '',
    tobaccoUseLastReportDate: '',
    tobaccoFreeStatus: '',
    hasPrepopulationError: false,
    hasRequiredError: false,
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    coveredIndividualId: '',
    isOffExchange: false,
    isOnExchange: false,
  },
  setLoginUserTobaccoIndicator(loginUserTobaccoIndicator: any) {
    set((state: any) => {
      const tempLoginUser = {
        ...state.loginUser,
        tobaccoIndicator: loginUserTobaccoIndicator,
        hasPrepopulationError:
          state.loginUser.tobaccoUseLastReportDate &&
          loginUserTobaccoIndicator === 'T'
            ? isOutsideOfDateRange(state.loginUser.tobaccoUseLastReportDate)
            : false,
        hasRequiredError:
          loginUserTobaccoIndicator === 'T'
            ? isEmpty(state.loginUser.tobaccoUseLastReportDate)
            : false,
      };

      return { loginUser: tempLoginUser };
    });
  },
  setLoginUserLastUseDate(loginUserLastUseDate: any) {
    set((state: any) => {
      const tempLoginUser = {
        ...state.loginUser,
        tobaccoUseLastReportDate: loginUserLastUseDate,
        hasPrepopulationError: isOutsideOfDateRange(loginUserLastUseDate),
        hasRequiredError: isEmpty(loginUserLastUseDate),
      };
      return { loginUser: tempLoginUser };
    });
  },
  dependents: [],
  setDependentTobaccoIndicator: (tobaccoInd: any, dependentId: any) => {
    set((state: TobaccoUseStore) => {
      const tempTobaccoList = [...state.dependents];
      tempTobaccoList[dependentId] = new Dependent(
        tempTobaccoList[dependentId].isOverTobaccoAgeLimit,
        tobaccoInd,
        tempTobaccoList[dependentId].tobaccoUseEffectiveBeginDate,
        tempTobaccoList[dependentId].tobaccoUseEffectiveEndDate,
        tempTobaccoList[dependentId].tobaccoUseLastReportDate,
        tempTobaccoList[dependentId].tobaccoFreeStatus,
        tempTobaccoList[dependentId].tobaccoUseLastReportDate &&
        tobaccoInd === 'T'
          ? isOutsideOfDateRange(
              tempTobaccoList[dependentId].tobaccoUseLastReportDate,
            )
          : false,
        tobaccoInd === 'T'
          ? isEmpty(tempTobaccoList[dependentId].tobaccoUseLastReportDate)
          : false,
        tempTobaccoList[dependentId].firstName,
        tempTobaccoList[dependentId].lastName,
        tempTobaccoList[dependentId].dateOfBirth,
        tempTobaccoList[dependentId].coveredIndividualId,
        tempTobaccoList[dependentId].isOffExchange,
        tempTobaccoList[dependentId].isOnExchange,
      );

      return { dependents: tempTobaccoList };
    });
  },
  setDependentLastUseDate: (dependentLastUseDate: any, dependentId: any) => {
    set((state: TobaccoUseStore) => {
      const tempTobaccoList = [...state.dependents];
      tempTobaccoList[dependentId] = new Dependent(
        tempTobaccoList[dependentId].isOverTobaccoAgeLimit,
        tempTobaccoList[dependentId].tobaccoIndicator,
        tempTobaccoList[dependentId].tobaccoUseEffectiveBeginDate,
        tempTobaccoList[dependentId].tobaccoUseEffectiveEndDate,
        dependentLastUseDate,
        tempTobaccoList[dependentId].tobaccoFreeStatus,
        state.offExchangeVal && dependentLastUseDate
          ? tempTobaccoList[dependentId].tobaccoIndicator === 'T' &&
            isOutsideOfDateRange(dependentLastUseDate)
          : false,
        isEmpty(dependentLastUseDate),
        tempTobaccoList[dependentId].firstName,
        tempTobaccoList[dependentId].lastName,
        tempTobaccoList[dependentId].dateOfBirth,
        tempTobaccoList[dependentId].coveredIndividualId,
        tempTobaccoList[dependentId].isOffExchange,
        tempTobaccoList[dependentId].isOnExchange,
      );

      return { dependents: tempTobaccoList };
    });
  },
  toggleIsDisabled: () => {
    set((state: TobaccoUseStore) => {
      let disabled = false;

      state.dependents.some((dependent: Dependent) => {
        if (
          (!dependent.tobaccoIndicator ||
            dependent.hasPrepopulationError ||
            dependent.hasRequiredError ||
            dependent.tobaccoIndicator === 'U' ||
            dependent.tobaccoIndicator.trim() === '' ||
            (dependent.tobaccoIndicator &&
              dependent.tobaccoIndicator === 'T' &&
              !dependent.tobaccoUseLastReportDate)) &&
          dependent.isOverTobaccoAgeLimit // check for errors only if the dependent is OverTobaccoAgeLimit and has the ability to change the answers
        )
          disabled = true;

        return disabled;
      });

      // if button is already disabled from dependents then do not check for login user - it is unnecessary
      if (!disabled) {
        if (
          !state.loginUser.tobaccoIndicator ||
          state.loginUser.hasPrepopulationError ||
          state.loginUser.hasRequiredError ||
          state.loginUser.tobaccoIndicator === 'U' ||
          state.loginUser.tobaccoIndicator.trim() === '' ||
          (state.loginUser.tobaccoIndicator &&
            state.loginUser.tobaccoIndicator === 'T' &&
            !state.loginUser.tobaccoUseLastReportDate)
        )
          disabled = true;
      }

      return {
        isDisabled: disabled,
      };
    });
  },
  initData: () => {
    // this will need to change whenever the new API is ready
    set((state: TobaccoUseStore) => {
      // const memberPlanApiData = state.memberPlanApiResponse.plans;

      const tobaccoApiData: Array<TobaccoInfo> =
        state.tobaccoApiResponse.tobaccoList;
      // create and populate a data structure for easy access to the tobacco data
      const mappedTobaccoApiData: Map<String, TobaccoInfo> = new Map();
      tobaccoApiData.forEach((item) => {
        mappedTobaccoApiData.set(item.member.coveredIndividualId, item);
      });

      let loginUserData: any = null;
      if (tobaccoApiData.length === 1) {
        [loginUserData] = tobaccoApiData; // fail safe for Subscriber being set as loginUser;
      } else {
        for (let i = 0; i <= tobaccoApiData.length - 1; i += 1) {
          if (tobaccoApiData[i].member.relationship === 'Subscriber') {
            // use a loop to find subscriber in case the order is wrong
            loginUserData = tobaccoApiData[i];
          }
        }
      }

      let loginUserToSave = state.loginUser;
      const dependentsToSave: Array<Dependent> = [];

      const currentDate = today(getLocalTimeZone());
      let loginUserLastUseDate: any;
      let loginUserOfTobaccoUseAge: any;
      let dependentOfTobaccoUseAge: any;

      if (loginUserData) {
        loginUserLastUseDate = mappedTobaccoApiData.get(
          loginUserData.member.coveredIndividualId,
        )?.tobaccoUseLastReportDate;

        if (
          !mappedTobaccoApiData.get(loginUserData.member.coveredIndividualId) // if there is no value for isOverTobaccoAgeLimit calculate if user is over 18 using birthdate
            ?.isOverTobaccoAgeLimit
        ) {
          const splitLoginUserBirthDate =
            loginUserData.member.birthDate.split('/');
          const compLoginUserBirthDate = new CalendarDate(
            Number(splitLoginUserBirthDate[2]),
            Number(splitLoginUserBirthDate[0]),
            Number(splitLoginUserBirthDate[1]),
          );
          loginUserOfTobaccoUseAge =
            compLoginUserBirthDate.add({ years: 18 }) <= currentDate;
        } else {
          // if field is not empty, just keep it as the data brought in from the API
          loginUserOfTobaccoUseAge = mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.isOverTobaccoAgeLimit;
        }
      } else {
        loginUserLastUseDate = undefined;
      }

      // TODO can we make this cleaner some how?
      if (loginUserData.member.relationship === 'Subscriber') {
        const dependents = tobaccoApiData.slice(1);
        dependents.forEach((dependent: any) => {
          const dependentLastReportDate = mappedTobaccoApiData.get(
            dependent.member.coveredIndividualId,
          )?.tobaccoUseLastReportDate;

          if (
            !mappedTobaccoApiData.get(dependent.member.coveredIndividualId) // if there is no value for isOverTobaccoAgeLimit calculate if user is over 18 using birthdate
              ?.isOverTobaccoAgeLimit
          ) {
            const splitDependentBirthDate =
              dependent.member.birthDate.split('/'); // create a CalendarDate format birthdate for comparison
            const compDependentBirthDate = new CalendarDate(
              Number(splitDependentBirthDate[2]),
              Number(splitDependentBirthDate[0]),
              Number(splitDependentBirthDate[1]),
            );
            dependentOfTobaccoUseAge =
              compDependentBirthDate.add({ years: 18 }) <= currentDate;
          } else {
            // if field is not empty, just keep it as the data brought in from the API
            dependentOfTobaccoUseAge = mappedTobaccoApiData.get(
              dependent.member.coveredIndividualId,
            )?.isOverTobaccoAgeLimit;
          }

          dependentsToSave.push(
            new Dependent(
              dependentOfTobaccoUseAge,
              mappedTobaccoApiData.get(
                dependent.member.coveredIndividualId,
              )?.tobaccoIndicator,
              mappedTobaccoApiData.get(
                dependent.member.coveredIndividualId,
              )?.tobaccoUseEffectiveBeginDate,
              mappedTobaccoApiData.get(
                dependent.member.coveredIndividualId,
              )?.tobaccoUseEffectiveEndDate,
              dependentLastReportDate,
              mappedTobaccoApiData.get(
                dependent.member.coveredIndividualId,
              )?.tobaccoFreeStatus,
              state.offExchangeVal && dependentLastReportDate
                ? mappedTobaccoApiData.get(dependent.member.coveredIndividualId)
                    ?.tobaccoIndicator === 'T' &&
                  isOutsideOfDateRange(dependentLastReportDate)
                : false,
              false, // data does not come in via api, set to false because we control this when a radio button is selected
              dependent.member.firstName,
              dependent.member.lastName,
              dependent.member.birthDate,
              dependent.member.coveredIndividualId,
              state.offExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
              state.onExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
            ),
          );
        });

        // the dependents sent by the API come in a different order sporadically
        // so sort them by first name so that the order is the same every time
        dependentsToSave.sort((a, b) => {
          const fa = a.firstName.toLowerCase();
          const fb = b.firstName.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });

        loginUserToSave = new Subscriber(
          loginUserOfTobaccoUseAge,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoIndicator,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoUseEffectiveBeginDate,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoUseEffectiveEndDate,
          loginUserLastUseDate,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoFreeStatus,
          // offExchange && loginUserLastUseDate
          //   ? isOutsideOfDateRange(loginUserLastUseDate)
          //   : false,
          state.offExchangeVal && loginUserLastUseDate
            ? mappedTobaccoApiData.get(loginUserData.member.coveredIndividualId)
                ?.tobaccoIndicator === 'T' &&
              isOutsideOfDateRange(loginUserLastUseDate)
            : false,
          // isEmpty(loginUserLastUseDate),
          false, // data does not come in via api, set to false because we control this when a radio button is selected
          loginUserData.member.firstName,
          loginUserData.member.lastName,
          loginUserData.member.dateOfBirth,
          loginUserData.member.coveredIndividualId,
          state.offExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
          state.onExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
          dependentsToSave,
        );
      } else {
        loginUserToSave = new LoginUser(
          loginUserOfTobaccoUseAge,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoIndicator,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoUseEffectiveBeginDate,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoUseEffectiveEndDate,
          loginUserLastUseDate,
          mappedTobaccoApiData.get(
            loginUserData.member.coveredIndividualId,
          )?.tobaccoFreeStatus,
          state.offExchangeVal && loginUserLastUseDate
            ? mappedTobaccoApiData.get(loginUserData.member.coveredIndividualId)
                ?.tobaccoIndicator === 'T' &&
              isOutsideOfDateRange(loginUserLastUseDate)
            : false,
          false, // data does not come in via api, set to false because we control this when a radio button is selected
          loginUserData.member.firstName,
          loginUserData.member.lastName,
          loginUserData.member.birthDate,
          loginUserData.member.coveredIndividualId,
          state.offExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
          state.onExchangeVal, // TODO update this once available on API - should this inherit from subscriber if blank?
        );
      }

      return {
        loginUser: loginUserToSave,
        dependents: dependentsToSave,
      };
    });
  },
  isBelowMinAgeLimit: false,
  setIsBelowMinAgeLimit: (state: any) =>
    set({
      isBelowMinAgeLimit: state,
    }),
  offExchangeVal: false,
  setOffExchangeVal: (state: any) =>
    set({
      offExchangeVal: state,
    }),

  onExchangeVal: false,
  setOnExchangeVal: (state: any) =>
    set({
      onExchangeVal: state,
    }),

  isDisabled: true,
  setIsDisabled: (state: any) =>
    set({
      isDisabled: state,
    }),
}));

export const useTobaccoStore = tobaccoStore;
